<template>
    <profile-section title="Share Price Activity">
        <div class="subtitleSymbol">
            <select
                v-if="multipleCompanies"
                v-model="selectCompanyIndex"
                class="symbolSelect"
                style="margin-bottom: 8px"
            >
                <option v-for="(companyStockPrice, index) in companyStockPrices" v-bind:key="index" :value="index">
                    {{ getCompanyTickerAndExchange(companyStockPrice) }}
                </option>
            </select>
            <span v-else>
                {{ chartData.exchange }}: <b>{{ chartData.ticker }}</b>
            </span>
        </div>
        <div class="subtitleTime">
            <span>FROM {{ chartData.startDate }} TO {{ chartData.endDate }}</span>
        </div>
        <div class="mt-chart" style="position: relative">
            <div class="sparklineLine">
                <Chart
                    ref="stockPriceChart"
                    @renderedChart="calculateOverlay"
                    :chart-data="chartData"
                    :options="options"
                    class="chart"
                />
            </div>
        </div>
    </profile-section>
</template>

<script>
import moment from 'moment'
import { toDictionary } from '@/utils/array'
import { stepSize } from '@/components/charts/utils'

import Chart from './chart.vue'
import ProfileSection from '@/components/shared/profile-section.vue'
import ChartColorCache from './chart-color-cache.js'

const colors = new ChartColorCache()

const X_AXIS_ID = 'x-axis-0'
const Y_AXIS_ID = 'y-axis-0'

export default {
    data() {
        return {
            dataXOffsetOverlay: null,
            selectCompanyIndex: this.selectedCompanyIndex,
        }
    },
    props: {
        companyStockPrices: {
            type: Array,
            require: true,
        },
        /** should be moment object */
        stockLineDate: {
            type: Object,
            default: null,
        },
        selectedCompanyIndex: {
            type: Number,
            default: 0,
        },
    },
    components: {
        Chart,
        ProfileSection,
    },
    computed: {
        multipleCompanies() {
            return this.companyStockPrices.length > 1
        },
        selectedCompanyStockPrices() {
            const selectedIndex = this.selectCompanyIndex
            return this.companyStockPrices[selectedIndex]
        },
        dateToStockPriceDictionary() {
            return toDictionary(
                this.selectedCompanyStockPrices.prices,
                price => moment(price.date).format('L').toString(),
                price => price.closePrice
            )
        },
        eventClosePrice() {
            if (this.stockLineDate) {
                let lastCloseDate = moment(this.stockLineDate)
                let closingPrice
                while ((closingPrice = this.dateToStockPriceDictionary[lastCloseDate.format('L')]) == undefined) {
                    lastCloseDate.subtract(1, 'days') // Remove day fall through to normal flow
                }

                return {
                    closingPrice,
                    lastCloseDate,
                }
            }

            return null
        },
        annotations() {
            let annotations = []
            if (this.stockLineDate) {
                annotations.push({
                    drawTime: 'afterDatasetsDraw',
                    type: 'line',
                    id: 'vline0', // if we add more needs to be dynamic
                    mode: 'vertical',
                    scaleID: X_AXIS_ID,
                    value: this.stockLineDate,
                    borderColor: 'red',
                    borderWidth: 1,
                    borderDash: [4, 4],
                })
            }

            return annotations
        },
        options() {
            const range = this.chartData.maxPrice - this.chartData.minPrice
            const stepper = this.getStepSize(range)
            const min = Math.floor(this.chartData.minPrice / stepper) * stepper
            const max = Math.ceil(this.chartData.maxPrice / stepper) * stepper

            return {
                animation: {
                    duration: 0,
                },

                legend: {
                    display: this.chartData.datasets.length > 1,
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                    },
                },
                scales: {
                    xAxes: [
                        {
                            distribution: 'linear',
                            type: 'time',
                            time: {
                                displayFormats: {
                                    quarter: 'MMM YYYY',
                                },
                                parser(arg0, arg1, arg2, arg3) {
                                    return moment(arg0)
                                },
                            },
                            id: X_AXIS_ID,
                            ticks: {
                                display: true,
                                zeroLineWidth: 5,
                            },
                            gridLines: {
                                display: true,

                                drawBorder: true,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            stacked: false,
                            id: Y_AXIS_ID,
                            ticks: {
                                min: min,
                                max: max,
                                display: true,
                                stepSize: stepper,
                                zeroLineWidth: 5,
                                zeroLineColor: 'BLACK',
                                callback: function (value, index, values) {
                                    return '$' + Math.round(value)
                                },
                            },
                            gridLines: {
                                display: true,

                                drawBorder: true,
                                color: '#E5E5E5',
                            },
                        },
                    ],
                },
                datasets: {
                    line: {
                        pointRadius: 0,
                        spanGaps: true,
                        borderCapStyle: 'round',
                        borderJoinStyle: 'round',
                        lineTension: 0,
                        bezierCurve: false,
                        fill: false,
                    },
                },
                annotation: {
                    drawTime: 'afterDatasetsDraw',
                    annotations: this.annotations,
                },
                tooltips: {
                    callbacks: {
                        title: function (context) {
                            return moment(context[0].xLabel).format('ll')
                        },
                        label: function (context, data) {
                            const eventClosePrice = this._data.eventClosePrice
                            const xDate = moment(context.xLabel)
                            const price = Number.parseFloat(context.value).toFixed(2)
                            const ticker = data.datasets[context.datasetIndex].label

                            if (
                                eventClosePrice &&
                                xDate > eventClosePrice.lastCloseDate &&
                                context.datasetIndex === 0
                            ) {
                                const percentChange =
                                    100 *
                                    ((context.value - eventClosePrice.closingPrice) / eventClosePrice.closingPrice)
                                const percentLabel =
                                    percentChange >= 0
                                        ? `${percentChange.toFixed(2)}%`
                                        : `(${Math.abs(percentChange).toFixed(2)}%)`
                                return `${ticker}: ${price} USD ${percentLabel}`
                            } else {
                                return `${ticker}: ${price} USD`
                            }
                        },
                        labelTextColor: function (context) {
                            const eventClosePrice = this._data.eventClosePrice
                            const xDate = moment(context.xLabel)
                            if (
                                eventClosePrice &&
                                xDate > eventClosePrice.lastCloseDate &&
                                context.datasetIndex === 0
                            ) {
                                const percentChange =
                                    100 *
                                    ((context.value - eventClosePrice.closingPrice) / eventClosePrice.closingPrice)
                                return percentChange < 0 ? 'red' : 'green'
                            }
                            return 'black'
                        },
                    },
                },
            }
        },
        chartData() {
            const prices = this.selectedCompanyStockPrices.prices
                .slice(0)
                .sort((lhs, rhs) => lhs.date.localeCompare(rhs.date))

            const result = {
                backgroundColor: 'grey',
                maxPrice: Number.MIN_VALUE,
                minPrice: Number.MAX_VALUE,
                exchange: prices[0].exchange,
                ticker: prices[0].ticker,
                startDate: moment(prices[0].date).format('M/D/YY'),
                endDate: moment(prices[prices.length - 1].date).format('M/D/YY'),
                eventClosePrice: this.eventClosePrice,
                datasets: [],
            }

            const companyPricePoints = []

            prices.forEach(price => {
                companyPricePoints.push({
                    x: price.date,
                    y: price.closePrice,
                })

                result.maxPrice = price.closePrice > result.maxPrice ? price.closePrice : result.maxPrice
                result.minPrice = price.closePrice < result.minPrice ? price.closePrice : result.minPrice
            })

            result.datasets.push({
                type: 'line',
                yAxisID: Y_AXIS_ID,
                tension: 0,
                // Boolean - whether or not the chart should be responsive and resize when the browser does.
                responsive: true,
                // Boolean - whether to maintain the starting aspect ratio or not when responsive, if set to false, will take up entire container
                maintainAspectRatio: false,
                label: prices[0].ticker,
                data: companyPricePoints,
                ...colors.getColors('company'),
            })

            const etfPrices = this.selectedCompanyStockPrices.etfPrices

            if (etfPrices?.length > 0) {
                const etfPricePoints = []

                etfPrices.forEach(price => {
                    etfPricePoints.push({
                        x: price.date,
                        y: price.closePrice,
                    })

                    result.maxPrice = price.closePrice > result.maxPrice ? price.closePrice : result.maxPrice
                    result.minPrice = price.closePrice < result.minPrice ? price.closePrice : result.minPrice
                })

                result.datasets.push({
                    type: 'line',
                    yAxisID: Y_AXIS_ID,
                    tension: 0,
                    // Boolean - whether or not the chart should be responsive and resize when the browser does.
                    responsive: true,
                    // Boolean - whether to maintain the starting aspect ratio or not when responsive, if set to false, will take up entire container
                    maintainAspectRatio: false,
                    label: etfPrices[0].ticker,
                    data: etfPricePoints,
                    ...colors.getColors('etf'),
                })
            }

            return result
        },
    },
    mounted() {
        window.addEventListener('resize', this.calculateOverlay)
    },
    unmounted() {
        window.removeEventListener('resize', this.calculateOverlay)
    },
    methods: {
        getStepSize(range) {
            return stepSize(range)
        },
        getCompanyTickerAndExchange(companyStockPrices) {
            const prices = companyStockPrices?.prices
            if (prices.length > 0) {
                const price = prices[0]
                return `${price.exchange}: ${price.ticker}`
            }
            return 'Unknown'
        },
        calculateOverlay() {
            var ref = this.$refs.stockPriceChart?.chart
            if (ref && this.stockLineDate) {
                var needle = this.stockLineDate.format('YYYY-MM-DDT00:00:00')
                var scale = ref.chart.scales[X_AXIS_ID]
                this.$data.dataXOffsetOverlay = scale.getPixelForValue(needle)
            }
        },
    },
}
</script>

<style>
@import '~@/css/charts.css';
.chart-title {
    position: relative;

    & h3.title {
        margin: 8px 0 8px 0;
    }

    & .subtitleSymbol {
        color: #666666;
        font-family: Lato;
        font-size: 12px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 16px;
        text-align: left;
        float: left;
        margin: 0 0 10px 0;
    }
    & .subtitleTime {
        color: #666666;
        font-family: Lato;
        font-size: 12px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 16px;
        text-align: right;
        float: right;
        margin: 0 0 10px 0;
    }
}

.mt-chart {
    margin: 32px 0 0 0;
}

.sparklineLine {
    padding: 0;
    margin: 0;
    width: 100%;
    height: 240px;
    position: relative;

    & .chart {
        padding: 0;
        margin: 0;
        width: 100%;
        height: 100%;
        & canvas {
            display: inline-block;
        }
    }
}
</style>
